import { Collections, UserPrivateInfoDefault, UserProfileDefault } from "api";
import { UserDefault } from "api/model";
import cloneDeep from "lodash/cloneDeep";
import { useSelector } from "react-redux";
import {
    OrderByOptions,
    ReduxFirestoreQuerySetting,
    useFirestoreConnect,
    WhereOptions,
} from "react-redux-firebase";
import { RootState } from "redux/model";

interface Options {
    id?: string;
    withPrivateInfo?: boolean;
    withRoles?: boolean;
    where?: WhereOptions | WhereOptions[];
    orderBy?: OrderByOptions;
    startAt?: any;
    endAt?: any;
}

export const useUsers = <T>(options?: Options): T => {
    const {
        id,
        withPrivateInfo = false,
        withRoles = false,
        where,
        orderBy,
        startAt,
        endAt,
    } = options || {};

    const queriesConfig: ReduxFirestoreQuerySetting[] = [
        {
            collection: Collections.USERS,
            doc: id,
            where,
            orderBy,
            startAt,
            endAt: endAt && `${endAt}\uf8ff`,
        },
    ];

    if (withPrivateInfo) {
        queriesConfig.push({
            collection: Collections.USERS_PRIVATE_INFO,
            doc: id,
        });
    }

    if (withRoles) {
        queriesConfig.push({
            collection: Collections.USERS_ROLES,
            doc: id,
        });
    }

    useFirestoreConnect(queriesConfig);

    const result = cloneDeep(
        useSelector((state: RootState) => {
            const users =
                state.firestore[!id ? "ordered" : "data"][Collections.USERS];

            const privateInfos =
                state.firestore.data[Collections.USERS_PRIVATE_INFO];

            const roles = state.firestore.data[Collections.USERS_ROLES];

            const userDefault = withPrivateInfo
                ? { ...UserPrivateInfoDefault }
                : { ...UserDefault };

            if (!users) {
                return id ? userDefault : [];
            }

            let result = cloneDeep(users);

            if (id) {
                if (!users[id]) {
                    return userDefault;
                }

                result = {
                    ...UserProfileDefault,
                    ...users[id],
                    id,
                };

                if (withPrivateInfo && privateInfos) {
                    result.privateInfo = {
                        ...UserPrivateInfoDefault,
                        ...privateInfos[id],
                    };
                }
                if (withRoles && roles && roles[id]) {
                    result.roles = [...roles[id].roles];
                }
            } else {
                if (withPrivateInfo && privateInfos) {
                    result.forEach(
                        (user) =>
                            (user.privateInfo = {
                                ...UserPrivateInfoDefault,
                                ...privateInfos[user.id],
                            })
                    );
                }
                if (withRoles && roles) {
                    result.forEach(
                        (user) =>
                            (user.roles = [...roles[user.id]?.roles] || [])
                    );
                }
            }

            return result;
        })
    );

    return result;
};
